// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMContextService } from './lm-context.service';
import * as urltemplate from 'url-template';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class LMLoggerService {
  private indentLevel = 0;
  private isIndented = true;
  private timeCounters: any = {};
  private _instanceId = 0;

  private loggerTimeInfoMap = new Map();

  constructor(
    private http: HttpClient,
    private lmContextService: LMContextService
  ) {
  }

  /**
   * Log text (preferably json) to the server, so it will get into the sumo logs.
   * @param logText
   */
  public logToServerConsole(logText: string): void {
    const urlCompiledTemplate = urltemplate.parse(bannerSpecUrl);
    const bannerUrl = urlCompiledTemplate.expand({
      urlPdlOrDa: this.lmContextService.getContext().urlPdlOrDa,
      webApp: this.lmContextService.getContext().webApp
    });

    const postContent: any = { requestType: 'logToServerConsole', log: logText };
    this.http.post(bannerUrl, postContent).pipe(
      catchError(error => throwError(error || 'Server error'))).subscribe();
  }

  // outputs server logs to client; requires csk.log > 0
  private get cskLogToClient(): boolean {
    return this.lmContextService.getLogToClient();
  }

  /**
   * see LoggerTraceLevel in Logger.java
   * @type {number}
   * 0 = no logging; same as not having the csk
   * 1 = log(), warn()
   * 2 = time(), timeEnd(), timeEndCheck() in addition to above
   * 3 = error(), stack() in addition to above
   * 4 = trace() in addition to above
   */
  public get cskLog(): number {
    return this.lmContextService.getLog();
  }

  private get isAnyTrackingEnabled(): boolean {
    return this.lmContextService.isAnyTrackingEnabled();
  }

  private get isMonocleEnabled(): boolean {
    return this.lmContextService.isMonocleEnabled();
  }

  public get instanceId(): string {
    return (this.cskLog > 0 || this.isAnyTrackingEnabled)
      ? 'id-' + this._instanceId++
      : '';
  }

  public error(value?: any) {
    try {
      if (console && this.cskLog > 2) {
        console.error(this.indent('ERROR: '), value);
      }
    } catch (e) {
    }
  }

  public log(value?: any) {
    try {
      if (console && this.cskLog > 0) {
        console.log(this.indent('  LOG: '), value);
      }
    } catch (e) {
    }
  }

  public serverLog(value?: any) {
    try {
      if (console && this.cskLogToClient && this.cskLog > 0) {
        console.groupCollapsed('SERVERLOG');
        this.log(value);
        console.groupEnd();
      }
    } catch (e) {
    }
  }

  public trace(value?: any) {
    try {
      if (console && this.cskLog > 3) {
        console.groupCollapsed('TRACE');
        // eslint-disable-next-line no-console
        console.trace(this.indent('TRACE: '), value);
        console.groupEnd();
      }
    } catch (e) {}
  }

  public warn(value?: any) {
    try {
      if (console && this.cskLog > 0) {
        if (console.warn) {
          console.warn(this.indent(' WARN: '), value);
        } else {
          console.log(this.indent(' WARN: '), value);
        }
      }
    } catch (e) {
    }
  }

  public time(loggerTimeInfo: LMLoggerTimeInfo) {
    if (!loggerTimeInfo) {
      throw 'LMLoggerService time() loggerTimeInfo is a required parameter';
    }

    if (console && this.cskLog > 1 || this.isAnyTrackingEnabled) {
      this.loggerTimeInfoMap.set(loggerTimeInfo.id, loggerTimeInfo);
      this.setTime('s', loggerTimeInfo.id);
      this.indentLevel++;
    }
  }

  public timeEnd(id: string, value?: any): number {
    if (!id) {
      throw 'LMLoggerService timeEnd() id is a required parameter';
    }

    if (console && this.cskLog > 1 || this.isAnyTrackingEnabled) {
      this.indentLevel--;

      if (this.indentLevel < 0) {
        this.indentLevel = 0;
      }

      return this.setTime('e', id, value);
    }
  }

  public timeEndCheck(id: string, maximum: number, value?: any) {
    if (!id) {
      throw 'LMLoggerService timeEndCheck() id is a required parameter';
    }

    if (maximum < 1) {
      throw 'LMLoggerService timeEndCheck() maximum must be greater than or equal to one';
    }

    if (console && this.cskLog > 1) {
      this.indentLevel--;

      if (this.indentLevel < 0) {
        this.indentLevel = 0;
      }

      const timeEnd = this.setTime('e', id, value);

      if (timeEnd >= maximum) {
        this.error([id, 'process took longer than maximum', timeEnd + ' >= ' + maximum]);
      }
    }
  }

  private setTime(type: string, id: string, value?: any): number {
    // provides consistent calculations regardless of browser as the
    // browser calculation is different than doing it ourselves
    if (type === 's') {
      const time = window.performance.now();
      const key = 'KEY' + id.toString();

      if (this.timeCounters[ key ]) {
        return;
      }

      this.logTime('START', id);
      this.timeCounters[ key ] = time;

    } else if (type === 'e') {
      const key = 'KEY' + id.toString();
      const timeCounter = this.timeCounters[ key ];

      if (timeCounter) {
        const time = window.performance.now();
        const diff = time - timeCounter;
        const green = '\x1b[32m';
        const cyan = '\x1b[36m';
        const yellow = '\x1b[33m';
        const red = '\x1b[31m';
        const loggerTimeInfo: LMLoggerTimeInfo = this.loggerTimeInfoMap.get(id);
        let msg: string;

        if (loggerTimeInfo) {
          msg = `${loggerTimeInfo.description ? loggerTimeInfo.description : loggerTimeInfo.id}`;

          if (loggerTimeInfo.fieldType !== undefined) {
            if (diff < 5) {
              msg = `${green} ${msg}`;
            }

            if (diff > 5 && diff < 10) {
              msg= `${cyan} ${msg}`;
            }

            if (diff >= 10 && diff < 15) {
              msg = `${yellow} ${msg}`;
            }

            if (diff >= 15) {
              msg = `${red} ${msg}`;
            }
          } else {
            if (diff < 10) {
              msg = `${green} ${msg}`;
            }

            if (diff >= 10 && diff < 100) {
              msg= `${cyan} ${msg}`;
            }

            if (diff >= 100 && diff < 500) {
              msg = `${yellow} ${msg}`;
            }

            if (diff > 500) {
              msg = `${red} ${msg}`;
            }
          }

          loggerTimeInfo.time = `${diff}ms`;
        } else {
          msg = id + ': ' + diff + 'ms';
        }

        this.logTime('', msg, value, loggerTimeInfo);

        delete this.timeCounters[ key ];
        return diff;
      }
    }
  }

  private logTime(prefix: string, value: string, timeEndValue?: any, loggerTimeInfo?: LMLoggerTimeInfo) {
    if (console && this.cskLog > 1) {
      try {
        if (prefix === 'START') {
          value = this.indent(prefix + ': ' + value);
          console.log(value, timeEndValue || '');
        }

        if (loggerTimeInfo) {
          console.groupCollapsed(this.indent(`  END:${value}`));
          console.table(loggerTimeInfo);
          console.groupEnd();
        }

      } catch (e) {}
    }
  }

  private indent(value?: any) {
    if (this.isIndented) {
      return '\t'.repeat(this.indentLevel) + value;
    }

    return value;
  }
}
